import React, { createContext, useContext, useState, useCallback } from "react";
import TutorialOverlay from "../Components/Other Components/tutorialOverlay";

const TutorialContext = createContext();

export const useTutorial = () => useContext(TutorialContext);

const STORAGE_KEY = "ustorage_tutorials_seen";

function loadSeen() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export const TutorialProvider = ({ children }) => {
  const [seenTutorials, setSeenTutorials] = useState(loadSeen);
  const [active, setActive] = useState(null); // { tutorialId, steps, stepIndex, isReplay }

  const hasSeen = useCallback(
    (tutorialId) => seenTutorials.includes(tutorialId),
    [seenTutorials]
  );

  const markSeen = useCallback((tutorialId) => {
    setSeenTutorials((prev) => {
      if (prev.includes(tutorialId)) return prev;
      const next = [...prev, tutorialId];
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // localStorage unavailable (private browsing, quota) - non-critical
      }
      return next;
    });
  }, []);

  const startTutorial = useCallback((tutorialId, steps, options = {}) => {
    if (!tutorialId || !Array.isArray(steps) || steps.length === 0) return;
    setActive({
      tutorialId,
      steps,
      stepIndex: 0,
      isReplay: options.isReplay ?? false,
    });
  }, []);

  // Only auto-starts if this tutorial hasn't been seen yet - the entry
  // point pages call on mount; replays always go through startTutorial
  // directly (via the help button) so they bypass this check.
  const maybeAutoStart = useCallback(
    (tutorialId, steps) => {
      if (!hasSeen(tutorialId)) {
        startTutorial(tutorialId, steps, { isReplay: false });
      }
    },
    [hasSeen, startTutorial]
  );

  const advanceStep = useCallback(() => {
    setActive((prev) => {
      if (!prev) return prev;
      const nextIndex = prev.stepIndex + 1;
      if (nextIndex >= prev.steps.length) {
        markSeen(prev.tutorialId);
        return null;
      }
      return { ...prev, stepIndex: nextIndex };
    });
  }, [markSeen]);

  // completed=true when the user finished the tour (or explicitly chose
  // to skip anyway past the warning) - either way it counts as "seen" so
  // it won't auto-start again. completed=false is reserved for cases
  // where we want to close without marking seen (not currently used, but
  // keeps the API honest).
  const endTutorial = useCallback(
    (completed = true) => {
      setActive((prev) => {
        if (prev && completed) markSeen(prev.tutorialId);
        return null;
      });
    },
    [markSeen]
  );

  return (
    <TutorialContext.Provider
      value={{
        active,
        hasSeen,
        startTutorial,
        maybeAutoStart,
        advanceStep,
        endTutorial,
      }}
    >
      {children}
      {active && <TutorialOverlay />}
    </TutorialContext.Provider>
  );
};
